import React, { useEffect } from "react";
import { X, AlertCircle, Sparkles, TrendingUp, ExternalLink } from "lucide-react";

export const ProjectModal = ({ project, onClose }) => {
  useEffect(() => {
    if (!project) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [project, onClose]);

  if (!project) return null;

  return (
    <div
      data-testid="project-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-modal-title"
      className="fb-modal-root fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      <div className="fb-modal-overlay absolute inset-0 bg-black/70 backdrop-blur-md" />
      <div
        className="fb-modal-card relative z-10 w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl border border-[#B026FF]/30 bg-[#0A0510]/90 backdrop-blur-2xl shadow-[0_30px_120px_-20px_rgba(176,38,255,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow accents */}
        <div className="pointer-events-none absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#B026FF]/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-[#6B0FBF]/25 blur-3xl" />

        {/* Close */}
        <button
          data-testid="project-modal-close"
          data-cursor="hover"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full border border-white/15 bg-black/40 backdrop-blur-md text-white/80 hover:text-white hover:border-[#B026FF]/60 hover:bg-[#B026FF]/15 transition flex items-center justify-center"
        >
          <X size={18} />
        </button>

        {/* Hero image */}
        <div className="relative h-56 sm:h-72 lg:h-80 overflow-hidden rounded-t-2xl">
          {project.image ? (
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[#05030A] flex items-center justify-center font-mono-ui text-xs text-white/55 relative">
              <div className="absolute inset-0 grid-bg opacity-40" />
              <div className="relative z-10 px-6 py-4 flex flex-col gap-2 text-left">
                {project.code?.map((line, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="text-[#B026FF]">›</span>
                    <span className="text-white/85">{line}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0510] via-transparent to-transparent" />
          <span
            className="absolute top-4 left-4 px-3 py-1 rounded-full border border-white/15 bg-black/50 backdrop-blur-md font-mono-ui text-[10px] uppercase tracking-widest"
            style={{ color: project.accent }}
          >
            {project.tag}
          </span>
        </div>

        {/* Body */}
        <div className="relative p-7 sm:p-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <h3
                id="project-modal-title"
                className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white"
              >
                {project.title}
              </h3>
              <p className="mt-3 text-white/70 text-base leading-relaxed max-w-2xl">
                {project.summary}
              </p>
            </div>
            <a
              href="#"
              data-cursor="hover"
              className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#B026FF] transition-colors font-mono-ui uppercase tracking-widest"
            >
              Visit live <ExternalLink size={14} />
            </a>
          </div>

          {/* 3-column case study */}
          <div className="grid md:grid-cols-3 gap-5">
            <CaseColumn
              testid="project-modal-problem"
              Icon={AlertCircle}
              label="The Problem"
              accent="text-rose-300"
              text={project.problem}
            />
            <CaseColumn
              testid="project-modal-solution"
              Icon={Sparkles}
              label="Our Solution"
              accent="text-[#B026FF]"
              text={project.solution}
            />
            <CaseColumn
              testid="project-modal-result"
              Icon={TrendingUp}
              label="The Result"
              accent="text-emerald-300"
              text={project.result}
              highlight
            />
          </div>

          {/* Metrics row */}
          {project.metrics && (
            <div className="mt-8 pt-7 border-t border-white/5 grid grid-cols-3 gap-4">
              {project.metrics.map((m, i) => (
                <div key={i} className="text-center sm:text-left">
                  <div className="font-display text-2xl sm:text-3xl font-bold text-white">
                    {m.value}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-white/45 font-mono-ui mt-1">
                    {m.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CaseColumn = ({ Icon, label, text, accent, highlight = false, testid }) => (
  <div
    data-testid={testid}
    className={`relative p-5 rounded-xl border ${
      highlight
        ? "border-[#B026FF]/40 bg-[#B026FF]/[0.06]"
        : "border-white/10 bg-white/[0.02]"
    }`}
  >
    <div className={`flex items-center gap-2 mb-3 ${accent}`}>
      <Icon size={16} strokeWidth={1.8} />
      <span className="text-[11px] uppercase tracking-[0.18em] font-mono-ui">
        {label}
      </span>
    </div>
    <p className="text-sm text-white/75 leading-relaxed">{text}</p>
  </div>
);
